import React, { useState } from 'react'

type Props = {
  impersonatedUser: {
    id: number
    name: string
    email: string
  }
  trueUser: {
    id: number
    name: string
    email: string
  }
  onStopImpersonating: () => void
}

export default function ImpersonationBanner({ impersonatedUser, trueUser, onStopImpersonating }: Props) {
  const [stopping, setStopping] = useState(false)

  const handleStop = async () => {
    if (!confirm(`Stop impersonating ${impersonatedUser.name}?`)) {
      return
    }

    setStopping(true)
    try {
      const { fetchWithCsrf } = await import('../utils/csrf')
      const response = await fetchWithCsrf('/api/v1/impersonate', {
        method: 'DELETE'
      })

      if (response.ok) {
        onStopImpersonating()
      } else {
        const error = await response.json()
        alert(error.error || 'Failed to stop impersonation')
      }
    } catch (err) {
      console.error('Error stopping impersonation:', err)
      alert('Failed to stop impersonation')
    } finally {
      setStopping(false)
    }
  }

  return (
    <div className="bg-yellow-50 border-b border-yellow-200">
      <div className="max-w-7xl mx-auto py-3 px-3 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between flex-wrap">
          <div className="w-0 flex-1 flex items-center">
            <span className="flex p-2 rounded-lg bg-yellow-400">
              <svg className="h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
            </span>
            <p className="ml-3 font-medium text-yellow-900">
              <span className="md:hidden">
                Viewing as {impersonatedUser.name}
              </span>
              <span className="hidden md:inline">
                You ({trueUser.name}) are viewing the site as <strong>{impersonatedUser.name}</strong> ({impersonatedUser.email})
              </span>
            </p>
          </div>
          <div className="order-3 mt-2 flex-shrink-0 w-full sm:order-2 sm:mt-0 sm:w-auto">
            <button
              onClick={handleStop}
              disabled={stopping}
              className="flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-yellow-900 bg-white hover:bg-yellow-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {stopping ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-yellow-900" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Stopping...
                </>
              ) : (
                'Stop Impersonating'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
